import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTransitionState } from 'react-transition-state';

type HeightTransitionOptions = {
  isOpen: boolean;
  duration?: number;
  unmountOnExit?: boolean;
};

export const useHeightTransition = ({
  isOpen,
  duration = 350,
  unmountOnExit = false,
}: HeightTransitionOptions) => {
  const contentRef = useRef<HTMLDivElement>(null);
  // Stores the open height captured while the element is fully visible.
  // Reading scrollHeight in preExit is unreliable because the 'collapsing'
  // class (height: 0) is already applied by the time the effect runs,
  // which can cause child elements to collapse and scrollHeight to read as 0.
  const openHeightRef = useRef<number>(0);

  const [{ status }, toggle] = useTransitionState({
    timeout: duration,
    preEnter: true,
    preExit: true,
    initialEntered: isOpen,
  });

  useEffect(() => {
    toggle(isOpen);
  }, [isOpen, toggle]);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    if (status === 'preEnter') {
      el.style.height = '0px';
      void el.offsetHeight;
      el.style.height = `${el.scrollHeight}px`;
    } else if (status === 'entering') {
      el.style.height = `${el.scrollHeight}px`;
    } else if (status === 'entered') {
      // Capture height while element is fully open with no CSS restrictions.
      openHeightRef.current = el.scrollHeight;
      el.style.height = '';
    } else if (status === 'preExit') {
      // Use the stored height — scrollHeight here is unreliable because
      // the 'collapsing' class is already applied with height: 0.
      el.style.height = `${openHeightRef.current}px`;
      void el.offsetHeight;
      el.style.height = '0px';
    } else if (status === 'exiting') {
      el.style.height = '0px';
    } else {
      el.style.height = '';
    }
  }, [status]);

  const isTransitioning =
    status === 'preEnter' ||
    status === 'entering' ||
    status === 'preExit' ||
    status === 'exiting';

  const shouldRender = !unmountOnExit || status !== 'exited';

  return { contentRef, status, isTransitioning, shouldRender };
};
